import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const useAppLanguage = () => {
  const [params, setParams] = useSearchParams();
  const { i18n } = useTranslation();

  const language = useMemo(() => {
    const lang = params.get('language');
    if (lang) return lang;
    
    // Fallback to moeda=brl -> br
    if (params.get('moeda') === 'brl') return 'br';
    
    // Fallback to i18n detected language
    const detected = i18n.language;
    if (detected?.startsWith('pt')) return 'br';
    if (detected?.startsWith('es')) return 'es';
    
    return 'en'; // final fallback
  }, [params, i18n.language]);

  const currency = useMemo(() => {
    if (language === 'br') return 'brl';
    return 'usd';
  }, [language]);

  useEffect(() => {
    const i18nLang = language === 'br' ? 'pt-BR' : (language === 'es' ? 'es-ES' : 'en-US');
    if (i18n.language !== i18nLang) {
      i18n.changeLanguage(i18nLang);
    }
  }, [language, i18n]);

  useEffect(() => {
    if (!params.get('language')) {
      const newParams = new URLSearchParams(params);
      newParams.set('language', language);
      newParams.delete('moeda'); // Cleanup legacy param if present
      setParams(newParams, { replace: true });
    }
  }, [language, params, setParams]);

  const changeLanguage = (newLang: string) => {
    const newParams = new URLSearchParams(params);
    newParams.set('language', newLang);
    newParams.delete('moeda'); // Remove legacy param
    setParams(newParams);
  };

  return { language, currency, changeLanguage };
};
